
import React from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../shared/components/badges/StatusBadge';
import MedicalStaffExpandedRow from '../components/MedicalStaffExpandedRow';
import { ProfileUpload } from '../../users/components/profileUpload';
import TabbedForm from '../../../shared/components/forms/FormTabs';
import { MedicalStaffService } from '../../../shared/services/medicalStaffService';
import { CenterService } from '../../../shared/services/centerService';
import apiService from '../../../shared/services/apiService';
import { getCenterOptions } from '../../../features/vaccines/stock/config/stockManagementConfig';

// -------------------------
// Shared Dropdown Loader
// -------------------------
const loadDropdownOptions = async (key, fallback = []) => {
  try {
    const res = await apiService.getAll('dropdowns');
    const data = res?.data?.[key] || res?.[key] || [];

    // Filter out 'ALL' option if present
    return data
      .filter(item => item.value !== 'ALL')
      .map(item => ({
        value: item.value,
        label: item.label
      }));
  } catch (error) {
    console.warn(`Failed to load ${key} from dropdowns API`, error);
    return fallback;
  }
};

// -------------------------
// Options Loaders
// -------------------------
export const getRoleOptions = () => loadDropdownOptions('roles');
export const getStatusOptions = () => loadDropdownOptions('employmentStatuses', [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'SUSPENDED', label: 'Suspended' }
]);
export const getDepartmentOptions = () => loadDropdownOptions('departments');

let centerCache = [];



// -------------------------
// Medical Staff Config
// -------------------------
export const medicalStaffConfig = {
  title: 'Medical Staff',
  endpoint: 'admin/medical-staff',
  storageKey: 'medicalStaff',
  icon: UserGroupIcon,
  useUrlNavigation: true,
  basePath: '/admin/medical-staff',
  statusOptionsLoader: getStatusOptions,
  statusFilterKey: 'employmentStatuses',

  // -------------------------
  // Grid Columns
  // -------------------------
  columns: [
    {
      key: 'profilePicture',
      label: 'Staff Member',
      render: (item) => {
        const imageSrc = item.profilePicture || null;
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 h-10 w-10">
              {imageSrc ? (
                <img src={imageSrc} alt={item.name || 'User'} className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-lg">👤</span>
                </div>
              )}
            </div>
            <div>{`${item.firstName} ${item.lastName}`}</div>
          </div>
        );
      }
    },
    { key: 'role', label: 'Role', render: (item) => item.role || '—' },
    { key: 'department', label: 'Department', render: (item) => item.department || '—' },
    { key: 'centerName', label: 'Vaccination Center', render: (item) => item.centerName || '—' },
    { key: 'experienceYears', label: 'Experience', render: (item) => `${item.experienceYears || 0} years` },
    { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
  ],

  // -------------------------
  // Form Fields
  // -------------------------
  formFields: [
    // Personal Tab
    {
      name: "profilePicture",
      label: "Profile Picture",
      accept: 'image/*',
      section: 'personal',
      fullWidth: true,
      uploadApi: MedicalStaffService.uploadProfilePhoto,
      customRender: (field, value, onChange, error) => (
        <ProfileUpload field={field} value={value} onChange={onChange} error={error} />
      ),
    },
    { name: 'firstName', label: 'First Name', type: 'text', section: 'personal', required: true },
    { name: 'lastName', label: 'Last Name', type: 'text', section: 'personal', required: true },
    { name: 'email', label: 'Email', type: 'email', section: 'personal', required: true },
    { name: 'phone', label: 'Phone', type: 'tel', section: 'personal', required: true },
    { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', section: 'personal', required: true },
    { name: 'address', label: 'Address', type: 'textarea', section: 'personal', fullWidth: true },

    // Professional Tab
    { name: 'role', label: 'Role', type: 'select', optionsLoader: getRoleOptions, section: 'professional', required: true },
    { name: 'department', label: 'Department', type: 'select', optionsLoader: getDepartmentOptions, section: 'professional', required: true },
    { name: 'clinicId', label: 'Vaccination Center', type: 'select', optionsLoader: getCenterOptions, section: 'professional', required: true },
    { name: 'specialization', label: 'Specialization', type: 'text', section: 'professional' },
    { name: 'licenseNumber', label: 'License Number', type: 'text', section: 'professional', required: true },
    { name: 'experienceYears', label: 'Experience (Years)', type: 'number', section: 'professional' },
    { name: 'qualifications', label: 'Qualifications', type: 'textarea', section: 'professional', fullWidth: true, required: true },

    // Employment Tab
    { name: 'joiningDate', label: 'Joining Date', type: 'date', section: 'employment', required: true },
    { name: 'salary', label: 'Salary', type: 'number', section: 'employment', required: true },
    { name: 'workingHours', label: 'Working Hours', type: 'text', section: 'employment', required: true },
    { name: 'status', label: 'Status', type: 'select', optionsLoader: getStatusOptions, section: 'employment', required: true },
    { name: 'medicalHistory', label: 'Medical History', type: 'textarea', section: 'employment', fullWidth: true },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'employment', fullWidth: true }
  ],

  searchFields: ['firstName', 'lastName', 'email', 'role', 'department', 'licenseNumber', 'centerId'],

  formState: { activeTab: 'personal' },

  // -------------------------
  // Custom Handlers
  // -------------------------
  customHandlers: {
    onView: async (item) => {
      const response = await MedicalStaffService.getById(item.id);
      const data = response?.data || response;
      
      if (data.vaccinationCenter) {
        data.clinicId = data.vaccinationCenter.id;
        data.centerName = data.vaccinationCenter.name;
      } else if (data.clinicId && typeof data.clinicId === 'string') {
        try {
          const centerRes = await CenterService.getById(data.clinicId);
          const center = centerRes?.data || centerRes;
          data.centerName = center.name;
        } catch (error) {
          console.error('Failed to fetch center name', error);
        }
      }
      
      return data;
    },

    afterSave: async (savedData) => {
      const data = savedData?.data || savedData;
      
      if (data.clinicId) {
        try {
          const centerRes = await CenterService.getById(data.clinicId);
          const center = centerRes?.data || centerRes;
          data.centerName = center.name || '—';
        } catch (error) {
          console.error('Failed to fetch center name after save');
          data.centerName = '—';
        }
      } else {
        data.centerName = '—';
      }
      
      return data;
    },

    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
      const tabsConfig = {
        personal: { label: 'Personal Info' },
        professional: { label: 'Professional Details' },
        employment: { label: 'Employment Info' },
      };
      return <TabbedForm formFields={formFields} formData={formData} handleFormChange={handleFormChange} renderFormField={renderFormField} tabsConfig={tabsConfig} activeTab={activeTab} setActiveTab={setActiveTab} />;
    },

    renderExpandedRow: (item) => <MedicalStaffExpandedRow item={item} />,

    processFormData: async (formData) => {
      let profilePictureUrl = formData.profilePicture;

      if (formData.profilePicture instanceof File) {
        const uploadResponse =
          await MedicalStaffService.uploadProfilePhoto(formData.profilePicture);
        profilePictureUrl = uploadResponse?.data?.url || '';
      }

      const payload = {
        ...formData,

        firstName: formData.firstName?.trim(),
        lastName: formData.lastName?.trim(),
        email: formData.email?.toLowerCase(),
        phone: formData.phone,

        role: formData.role?.value ?? formData.role,
        clinicId: formData.clinicId?.value ?? formData.clinicId,
        status: formData.status?.value ?? formData.status,

        licenseNumber: formData.licenseNumber,
        experienceYears: Number(formData.experienceYears || 0),
        salary: Number(formData.salary || 0),

        profilePicture: profilePictureUrl,
      };

      return payload;
    },

    transformData: async (response) => {
      let items = [];

      if (Array.isArray(response?.data)) {
        items = response.data;
      } else if (Array.isArray(response?.data?.medicalStaff)) {
        items = response.data.medicalStaff;
      } else if (response?.data) {
        items = [response.data];
      }

      const itemsWithCenters = await Promise.all(
        items.map(async (item) => {
          let centerName = item.vaccinationCenter?.name || item.centerName || '—';
          
          if ((!centerName || centerName === '—') && item.clinicId) {
            try {
              const centerRes = await CenterService.getById(item.clinicId);
              const center = centerRes?.data || centerRes;
              centerName = center.name || '—';
            } catch (error) {
              console.error('Failed to fetch center name');
            }
          }

          return {
            ...item,
            role: item.role || '—',
            department: item.department || '—',
            centerName,
            status: item.status || 'ACTIVE'
          };
        })
      );

      return itemsWithCenters;
    }
  }
};
